// Chat Input Component
// Handles all types of user input (text, yes/no, select, date, file, phone)

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Send, Upload, CalendarIcon, Loader2, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import ConsultationScheduler from './ConsultationScheduler';

const ChatInput = ({
  question,
  completed,
  showScheduler,
  selectedConsultDate,
  setSelectedConsultDate,
  availableSlots,
  selectedSlot,
  setSelectedSlot,
  fetchAvailableSlots,
  handleScheduleConsultation,
  setShowScheduler,
  inputValue,
  setInputValue,
  selectedDate,
  setSelectedDate,
  uploading,
  smsConsent,
  setSmsConsent,
  formData,
  setFormData,
  handleAnswer,
  handleFileUpload,
  resetChat
}) => {
  // Completed state - show scheduler or thank you message
  if (completed && showScheduler) {
    return (
      <ConsultationScheduler
        selectedConsultDate={selectedConsultDate}
        setSelectedConsultDate={setSelectedConsultDate}
        availableSlots={availableSlots}
        selectedSlot={selectedSlot}
        setSelectedSlot={setSelectedSlot}
        fetchAvailableSlots={fetchAvailableSlots}
        handleScheduleConsultation={handleScheduleConsultation}
        onSkip={() => setShowScheduler(false)}
      />
    );
  }

  if (completed) {
    return (
      <div className="text-center">
        <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <CheckCircle className="w-6 h-6 text-teal-600" />
        </div>
        <p className="text-slate-600 text-sm mb-3">Thank you for your submission!</p>
        <Button variant="outline" size="sm" onClick={resetChat} data-testid="start-new-btn">
          Start New Inquiry
        </Button>
      </div>
    );
  }

  // Yes/No questions (including greeting)
  if (question?.type === 'yesno' || question?.id === 'greeting') {
    return (
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          className="flex-1"
          onClick={() => handleAnswer(true, 'Yes')}
          data-testid="answer-yes-btn"
        >
          Yes
        </Button>
        <Button 
          variant="outline" 
          className="flex-1"
          onClick={() => handleAnswer(false, 'No')}
          data-testid="answer-no-btn"
        >
          No
        </Button>
      </div>
    );
  }

  // Select dropdown questions
  if (question?.type === 'select') {
    return (
      <Select onValueChange={(value) => {
        const option = question.options.find(o => o.value === value);
        handleAnswer(value, option?.label || value);
      }}>
        <SelectTrigger data-testid="select-input">
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>
          {question.options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Date picker questions
  if (question?.type === 'date') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-start" data-testid="date-picker-btn">
            <CalendarIcon className="w-4 h-4 mr-2" />
            {selectedDate ? format(selectedDate, 'PPP') : 'Select a date'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => {
              setSelectedDate(date);
              if (date) {
                handleAnswer(format(date, 'yyyy-MM-dd'), format(date, 'PPP'));
              }
            }}
            disabled={(date) => date > new Date()}
          />
        </PopoverContent>
      </Popover>
    );
  }

  // File upload questions
  if (question?.type === 'file') {
    return (
      <div className="space-y-2">
        <Label htmlFor="file-upload" className="cursor-pointer">
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-teal-500 transition-colors">
            {uploading ? (
              <Loader2 className="w-6 h-6 text-teal-500 mx-auto animate-spin" />
            ) : (
              <>
                <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">Click to upload files</p>
              </>
            )}
          </div>
          <input
            id="file-upload"
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx"
            className="hidden"
            onChange={(e) => handleFileUpload(Array.from(e.target.files))}
            disabled={uploading}
            data-testid="file-input"
          />
        </Label>
        <Button 
          variant="ghost" 
          className="w-full text-slate-500"
          onClick={() => handleAnswer([], 'No files uploaded')}
          disabled={uploading}
          data-testid="skip-upload-btn"
        >
          Skip this step
        </Button>
      </div>
    );
  }

  // Phone input with TCPA SMS consent checkbox
  if (question?.type === 'phone') {
    return (
      <div className="space-y-3">
        <form 
          onSubmit={(e) => {
            e.preventDefault();
            if (inputValue.trim()) {
              setFormData(prev => ({ ...prev, sms_consent: smsConsent }));
              handleAnswer(inputValue.trim());
            }
          }}
          className="flex gap-2"
        >
          <Input
            type="tel"
            placeholder="(555) 555-5555"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="flex-1"
            data-testid="phone-input"
          />
          <Button 
            type="submit" 
            size="icon"
            className="bg-teal-500 hover:bg-teal-600"
            disabled={!inputValue.trim()}
            data-testid="send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
        
        {/* TCPA SMS Consent Checkbox */}
        <label className="flex items-start gap-2 text-xs text-slate-500 cursor-pointer p-2 bg-slate-50 rounded-lg border border-slate-200">
          <input
            type="checkbox"
            checked={smsConsent}
            onChange={(e) => setSmsConsent(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-500 focus:ring-teal-500"
            data-testid="sms-consent-checkbox"
          />
          <span>
            I consent to receive text messages about my case. Message & data rates may apply. Reply STOP to unsubscribe.
          </span>
        </label>
      </div>
    );
  }

  // Default text/email input
  return (
    <form 
      onSubmit={(e) => {
        e.preventDefault();
        if (inputValue.trim()) {
          handleAnswer(inputValue.trim());
        }
      }}
      className="flex gap-2"
    >
      <Input
        type={question?.type === 'email' ? 'email' : 'text'}
        placeholder="Type your answer..."
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        className="flex-1"
        data-testid="text-input"
      />
      <Button 
        type="submit" 
        size="icon"
        className="bg-teal-500 hover:bg-teal-600"
        disabled={!inputValue.trim()}
        data-testid="send-btn"
      >
        <Send className="w-4 h-4" />
      </Button>
    </form>
  );
};

export default ChatInput;
